import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Mail, MapPin, Clock, Award, Home } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import MethodologyCallout from '@/components/MethodologyCallout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import ServiceSchema from '@/components/schema/ServiceSchema';
import { pages as pageContent } from '@/data/pageContent';

const DomesticEPCExeter = () => {
  // pageContent is the single source of truth for FAQs (and their FAQPage
  // schema) on this route — the SSG build script consumes the same module,
  // so build-time HTML and React runtime render an identical FAQ list.
  const routeContent = pageContent['/domestic-epc-exeter'];
  const faqs = routeContent.faqs.map((f) => ({ question: f.q, answer: f.a }));

  const exeterAreas = [
    "EX1 - Exeter City Centre, Cathedral Quarter, Quayside",
    "EX2 - St Thomas, Alphington, Ide, Shillingford",
    "EX4 - Heavitree, Whipton, Pinhoe, Polsloe",
    "EX5 - Clyst St Mary, Topsham (nearby)"
  ];

  return (
    <>
      <Helmet>
        <title>Domestic EPC Exeter | Energy Performance Certificates | £120+ | OCDEA Qualified</title>
        <meta name="description" content="Professional Domestic EPC assessments in Exeter from £120. OCDEA qualified assessors covering all EX postcodes. Fast 48-hour turnaround. Book your Exeter EPC today - call 01392 980077." />
        <meta name="keywords" content="domestic EPC Exeter, EPC Exeter, energy certificate Exeter, EPC EX1, EPC EX2, EPC EX4, Exeter energy assessor, OCDEA Exeter, EPC cost Exeter, domestic energy certificate Exeter" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/domestic-epc-exeter" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Domestic Energy Performance Certificate (EPC)",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Energy Performance Direct",
              "telephone": "+44-1392-980077",
              "email": "info@energyperformancedirect.co.uk",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Exeter",
                "addressRegion": "Devon",
                "postalCode": "EX1",
                "addressCountry": "GB"
              }
            },
            "areaServed": {
              "@type": "City",
              "name": "Exeter"
            },
            "offers": {
              "@type": "Offer",
              "price": "120",
              "priceCurrency": "GBP",
              "priceSpecification": {
                "@type": "PriceSpecification",
                "minPrice": "120"
              }
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Homeowners, Landlords, Estate Agents in Exeter"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-green-50 via-cyan-50 to-green-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-green-600">Serving All Exeter Postcodes</span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                Domestic EPC Assessments in Exeter
              </h1>
              <p className="text-lg text-gray-600 mb-6">
                Professional Energy Performance Certificates for Exeter homes from £120. OCDEA qualified assessors covering EX1, EX2, EX4 & EX5. Fast 48-hour certificate delivery.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link to="/booking">
                  <Button className="text-white hover:opacity-90 px-8 py-6 text-lg rounded-full shadow-lg" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
                    Book Exeter EPC Assessment
                  </Button>
                </Link>
                <a href="tel:01392980077">
                  <Button variant="outline" className="border-2 border-green-600 text-green-600 hover:bg-green-50 px-8 py-6 text-lg rounded-full">
                    <Phone className="w-5 h-5 mr-2" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Award className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">OCDEA Qualified</h3>
                  <p className="text-sm text-gray-600">Elmhurst & Quidos accredited Exeter assessors</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Clock className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">Fast Turnaround</h3>
                  <p className="text-sm text-gray-600">48-hour certificate delivery guaranteed</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <MapPin className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">Local Service</h3>
                  <p className="text-sm text-gray-600">Based in Exeter, covering all EX postcodes</p>
                </CardContent>
              </Card>
              <Card className="border-none shadow-lg hover:shadow-xl transition-all">
                <CardContent className="p-6 text-center">
                  <Home className="w-12 h-12 text-cyan-500 mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">From £120</h3>
                  <p className="text-sm text-gray-600">Transparent pricing, no hidden fees</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Domestic EPC Services in Exeter</h2>
              
              <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
                <p>
                  Energy Performance Direct provides professional Domestic Energy Performance Certificate (EPC) assessments for residential properties throughout Exeter and the surrounding areas. Based in Exeter since 2009, we are local, independent energy assessors with extensive experience surveying Exeter's diverse housing stock—from Victorian terraces in St Thomas to modern apartments in the city centre and period properties in Heavitree.
                </p>

                <p>
                  A Domestic EPC is legally required whenever you sell or rent a property in Exeter. The certificate rates your home's energy efficiency on a scale from A (most efficient) to G (least efficient) and provides practical recommendations for improving energy performance, reducing bills, and meeting MEES (Minimum Energy Efficiency Standards) compliance for rental properties. Our Exeter-based assessors understand local property types, construction methods, and the specific challenges facing Exeter homeowners and landlords.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Why Choose Energy Performance Direct for Your Exeter EPC?</h3>

                <p>
                  As an established Exeter business operating since 2009, we have built a reputation for reliable, professional service with fast turnaround times and competitive pricing. All our assessors are OCDEA (On-Construction Domestic Energy Assessor) qualified and accredited through Elmhurst Energy and Quidos, ensuring your EPC meets all legal requirements and is accepted by estate agents, solicitors, and local authorities across Exeter and Devon.
                </p>

                <div className="bg-green-50 border-l-4 border-green-600 p-6 my-8">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">What Our Exeter Clients Say:</h4>
                  <p className="text-gray-700 italic mb-2">
                    "I've used Energy Performance Direct on many occasions and the EPC was done speedily and efficiently as usual. Excellent Service 5 Stars."
                  </p>
                  <p className="text-sm text-gray-600">— Mark Z., Exeter (FreeIndex Review)</p>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Exeter Areas We Cover</h3>

                <p>
                  We provide Domestic EPC assessments across all Exeter postcodes with no or minimal travel surcharge for properties within the Exeter area. Our local knowledge means we understand the unique characteristics of Exeter's housing, from Georgian townhouses near the Cathedral to 1930s semi-detached homes in Wonford and new-build developments in Monkerton.
                </p>

                <div className="bg-white border border-gray-200 rounded-lg p-6 my-8">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">Exeter Postcodes Covered:</h4>
                  <ul className="space-y-2">
                    {exeterAreas.map((area, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{area}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-gray-600 mt-4">
                    We also cover nearby areas including Topsham, Exminster, Woodbury, Broadclyst, Pinhoe, and villages within the Exeter periphery.
                  </p>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">The EPC Assessment Process in Exeter</h3>

                <p>
                  Our Exeter EPC assessment is straightforward and non-invasive. An OCDEA qualified assessor will visit your property at a time convenient to you (typically within 2-3 working days of booking) and conduct a thorough survey of your home. The assessment usually takes 30-60 minutes for a standard Exeter property and includes:
                </p>

                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Measuring rooms and noting property dimensions</li>
                  <li>Recording construction type (solid wall, cavity wall, timber frame)</li>
                  <li>Assessing insulation levels (walls, roof, floors)</li>
                  <li>Evaluating heating system type and controls (combi boiler, storage heaters, etc.)</li>
                  <li>Checking glazing type (single, double, or triple glazed windows)</li>
                  <li>Noting any renewable energy features (solar panels, heat pumps)</li>
                  <li>Photographing key energy features for the EPC register</li>
                </ul>

                <p>
                  Once the survey is complete, we process your EPC and lodge it with the official government EPC register within 48 hours. You receive your certificate by email, ready to provide to estate agents, solicitors, or tenants. The EPC is valid for 10 years and remains attached to the property, not the owner.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Domestic EPC Pricing for Exeter Properties</h3>

                <p>
                  Our Exeter Domestic EPC pricing is transparent and competitive. Standard pricing starts from £120 for properties up to 5 rooms/zones within Exeter (EX1, EX2, EX4 postcodes). Larger properties (6-10 rooms) are priced at £140-£180 depending on size and complexity. Properties in outlying Exeter areas (EX5, Topsham, Exminster) may have a small travel surcharge applied based on distance.
                </p>

                <p>
                  We offer discounts for landlords with multiple Exeter properties requiring EPCs. Contact us on 01392 980077 or email info@energyperformancedirect.co.uk to discuss your requirements and receive a tailored quote.
                </p>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Landlords: MEES Compliance in Exeter</h3>

                <p>
                  If you are a landlord renting out property in Exeter, you must comply with the Minimum Energy Efficiency Standards (MEES). Since April 2020, it has been illegal to rent out a property with an EPC rating below E unless you have a valid exemption. Our Exeter EPCs clearly show your property's rating and provide practical, cost-effective recommendations for improving energy efficiency if your property falls below the legal minimum.
                </p>

                <p>
                  We also offer <Link to="/services/domestic_advice" className="text-green-600 hover:text-green-700 underline">Domestic Energy Advice Reports</Link> for Exeter landlords who need detailed guidance on improving EPC ratings, estimating improvement costs, and navigating MEES compliance requirements.
                </p>

                <div className="bg-cyan-50 border-l-4 border-cyan-600 p-6 my-8">
                  <h4 className="text-xl font-bold text-gray-900 mb-3">Need Floor Plans Too?</h4>
                  <p className="text-gray-700 mb-4">
                    Many Exeter estate agents and landlords require professional floor plans alongside EPCs for property marketing. We provide <Link to="/floor-plans-exeter" className="text-cyan-600 hover:text-cyan-700 underline font-semibold">Floor Plans in Exeter</Link> measured to RICS standards, perfect for rightmove listings, brochures, and property portals.
                  </p>
                  <Link to="/floor-plans-exeter">
                    <Button variant="outline" className="border-cyan-600 text-cyan-600 hover:bg-cyan-50">
                      View Floor Plan Services
                    </Button>
                  </Link>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Book Your Exeter Domestic EPC Today</h3>

                <p>
                  Ready to book your Domestic EPC assessment in Exeter? Use our <Link to="/booking" className="text-green-600 hover:text-green-700 underline font-semibold">online booking system</Link> to select your preferred date and time, or call us on 01392 980077 to speak directly with our team. We typically offer appointments within 2-3 working days across all Exeter postcodes, with your certificate delivered within 48 hours of the assessment.
                </p>

                <p>
                  For urgent EPCs (property completion deadlines, tenancy start dates), please call us to discuss same-day or next-day availability. We understand the importance of timely service for Exeter property transactions and strive to accommodate tight deadlines wherever possible.
                </p>
              </div>

              {/* FAQ Section */}
              <div className="mt-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions - Exeter Domestic EPCs</h2>
                <div className="space-y-6">
                  {faqs.map((faq, idx) => (
                    <Card key={idx} className="border-none shadow-md">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h3>
                        <p className="text-gray-700">{faq.answer}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* CTA Section */}
              <div className="mt-16 rounded-2xl p-8 text-white text-center" style={{ background: 'linear-gradient(to right, #16a34a, #06b6d4)' }}>
                <h2 className="text-3xl font-bold mb-4">Ready to Book Your Exeter EPC?</h2>
                <p className="text-lg mb-6">
                  Fast, professional Domestic EPC assessments across all Exeter postcodes. Call 01392 980077 or book online now.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/booking">
                    <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full">
                      Book Online Now
                    </Button>
                  </Link>
                  <a href="tel:01392980077">
                    <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-green-600 px-8 py-6 text-lg rounded-full">
                      <Phone className="w-5 h-5 mr-2" />
                      Call 01392 980077
                    </Button>
                  </a>
                </div>
              </div>

              {/* Related Services */}
              <div className="mt-16">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Services in Exeter</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="border-none shadow-md hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <h4 className="text-lg font-bold mb-2">Floor Plans Exeter</h4>
                      <p className="text-sm text-gray-600 mb-4">Professional measured floor plans for Exeter properties</p>
                      <Link to="/floor-plans-exeter">
                        <Button variant="outline" className="w-full">Learn More</Button>
                      </Link>
                    </CardContent>
                  </Card>
                  <Card className="border-none shadow-md hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <h4 className="text-lg font-bold mb-2">Energy Advice Reports</h4>
                      <p className="text-sm text-gray-600 mb-4">MEES compliance guidance for Exeter landlords</p>
                      <Link to="/services/domestic_advice">
                        <Button variant="outline" className="w-full">Learn More</Button>
                      </Link>
                    </CardContent>
                  </Card>
                  <Card className="border-none shadow-md hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <h4 className="text-lg font-bold mb-2">All Domestic Services</h4>
                      <p className="text-sm text-gray-600 mb-4">View our full range of Exeter services</p>
                      <Link to="/services">
                        <Button variant="outline" className="w-full">View Services</Button>
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              </div>

            </div>
          </div>
        </section>

        <MethodologyCallout
          context="Where a domestic EPC is the starting point for a landlord's MEES advice engagement,"
          reportCode="DEAR (Domestic Energy Advice Report)"
        />

        <Footer />
      </div>
    </>
  );
};

export default DomesticEPCExeter;
